import type { LoanApplicationData } from "../../hooks/useNbiForm";
import { stripNonDigits, titleCase } from "./common";
import { extractReferences } from "./parserUtils";
import type {
    IncomePayload,
    PAYGIncome,
    PayCycleDetail,
    RentalIncome,
    SelfEmployedIncome,
    SetFieldValueCallback,
} from "./models";

const businessStructureMap: Record<string, string> = {
    "partnership": "Partnership",
    "company": "Company",
    "trust": "Trust",
};

const normalizeBusinessStructure = (value: string | undefined): string | undefined => {
    if (!value) return value;
    return businessStructureMap[value] ?? value;
};

const getAbnFromContractor = (currentAbn: string, employments: any[], applicantId: string) => {
    if (currentAbn) return { abn: currentAbn, ref: null };
    const contractor = employments.find(
        (e: any) => e.applicantId === applicantId && e.employmentType === "Contractor" && e.businessAbn
    );
    if (!contractor) return { abn: "", ref: null };
    return { abn: contractor.businessAbn, ref: contractor.references?.get("businessAbn") };
};

const handlePAYGIncomeDocument = (
    data: PAYGIncome,
    currentFormData: LoanApplicationData,
    setFieldValue: SetFieldValueCallback,
    applicantId: string | null,
    employmentId: string | null,
    defaultCurrency: string = "AUD"
): void => {
    const existingIncomes = currentFormData.incomes || [];
    if (applicantId === null) {
        console.log(
            `Skip payincome because applicant doesn't exist. Application: ${applicantId} DocumentId: ${data.documentId}`
        );
        return;
    }
    if (employmentId === null && data.documentId !== null) {
        console.log(
            `Skip payincome because employment doesn't exist for document-backed income. Employment: ${employmentId} DocumentId: ${data.documentId}`
        );
        return;
    }
    const existingPAYGIndex = existingIncomes.findIndex(
        // de-duplicate by id first, then by employerName per applicant
        (income) =>
            income.incomeType === "PAYG income" &&
            (income.id === data.id.toString() ||
                (income.employerName === data.employerName &&
                    ((income as any).applicantId ? (income as any).applicantId === applicantId : true)))
    );
    const paymentReferences = (payment: PayCycleDetail) => extractReferences(payment);

    // Prepare new payments from current document
    const newPayments = data.payments.map((payment) => ({
        id: payment.id ?? `payment_${payment.payDate}`,
        paygIncomeId: data.id.toString(),
        payDate: payment.payDate,
        grossPay: payment.grossPay,
        netPay: payment.netPay,
        baseWage: payment.baseWage,
        ytdGross: payment.ytdGross || 0,
        overtimeAmount: payment.overtimeAmount || 0,
        commissionAmount: payment.commissionAmount || 0,
        bonusAmount: payment.bonusAmount || 0,
        references: paymentReferences(payment),
    }));

    // Merge with existing payments if this is an update
    let mergedPayments = newPayments;
    if (existingPAYGIndex >= 0) {
        const existingPAYGIncome = existingIncomes[existingPAYGIndex] as Extract<
            LoanApplicationData["incomes"][number],
            { incomeType: "PAYG income" }
        >;
        const existingPayments = existingPAYGIncome.payments || [];

        // Combine existing and new payments, avoiding duplicates based on pay_date
        const paymentMap = new Map();

        // Add existing payments first
        existingPayments.forEach((payment) => {
            paymentMap.set(payment.payDate, payment);
        });

        // Add new payments (will overwrite if same pay_date)
        newPayments.forEach((payment) => {
            paymentMap.set(payment.payDate, payment);
        });

        mergedPayments = Array.from(paymentMap.values());
    }
    const references = extractReferences(data, existingIncomes[existingPAYGIndex]?.references);

    const paygIncomeData = {
        id: existingPAYGIndex >= 0 ? existingIncomes[existingPAYGIndex].id : data.id.toString() || `payg_${Date.now()}`,
        applicantId: applicantId,
        incomeType: "PAYG income" as const,
        employerId: employmentId ?? undefined,
        paygType: data.paygType,
        documentIssueDate: data.documentIssueDate,
        employerName: titleCase(data.employerName),
        payFrequency: data.payFrequency,
        isMedicalEmergencyOccupation: data.isMedicalEmergencyOccupation,
        currency: data.currency || defaultCurrency,
        payments: mergedPayments,
        documents: existingPAYGIndex >= 0 ? existingIncomes[existingPAYGIndex].documents : [],
        references: references,
    };

    let updatedIncomes: LoanApplicationData["incomes"];
    if (existingPAYGIndex >= 0) {
        // Update existing PAYG income
        updatedIncomes = [...existingIncomes];
        updatedIncomes[existingPAYGIndex] = paygIncomeData;
    } else {
        // Add new PAYG income
        updatedIncomes = [...existingIncomes, paygIncomeData];
    }

    setFieldValue("incomes", updatedIncomes);

    // here I'm getting business abn and payg_type/employment type from other places cuz we want to display that
    // arbitrary employment view that might or might not have things that come only from contract.
    const businessAbn = stripNonDigits(data.businessAbn);
    try {
        const existingEmployments = Array.isArray(currentFormData.employments) ? currentFormData.employments : [];
        const employmentIndex = existingEmployments.findIndex(
            (e) => e.id === employmentId && e.applicantId === applicantId
        );
        if (employmentIndex >= 0) {
            const updatedEmployments = [...currentFormData.employments];
            const existingEmployment = updatedEmployments[employmentIndex];
            const updates: any = {};

            if (businessAbn && businessAbn !== "null") {
                updates.businessAbn = businessAbn;
            }

            if (data.paygType) {
                updates.employmentType = data.paygType;
            }

            updatedEmployments[employmentIndex] = {
                ...existingEmployment,
                ...updates,
            };
            setFieldValue("employments" as any, updatedEmployments);
        }
    } catch (e) {
        console.error("Error parsing business ABN from PAYG income document:", e);
    }
};

const handleRentalIncomeDocument = (
    data: RentalIncome,
    currentFormData: LoanApplicationData,
    setFieldValue: SetFieldValueCallback,
    applicantId: string | null,
    defaultCurrency: string = "AUD"
): void => {
    if (applicantId === null) {
        console.log(
            `Skip payincome because applicant doesn't exist. Application: ${applicantId} DocumentId: ${data.documentId}`
        );
        return;
    }

    const existingIncomes = currentFormData.incomes || [];
    const existingRentalIndex = existingIncomes.findIndex(
        (income) =>
            income.incomeType === "Rental income" &&
            (income.id === data.id.toString() ||
                (income.propertyAddress === data.propertyAddress &&
                    ((income as any).applicantId ? (income as any).applicantId === applicantId : true)))
    );
    const references = extractReferences(data, existingIncomes[existingRentalIndex]?.references);
    const rentalIncomeData = {
        id:
            existingRentalIndex >= 0
                ? existingIncomes[existingRentalIndex].id
                : data.id.toString() || `rental_${Date.now()}`,
        applicantId: applicantId,
        incomeType: "Rental income" as const,
        rentalType: data.rentalType,
        propertyAddress: data.propertyAddress || "",
        documentIssueDate: data.documentIssueDate || "",
        rentalAmount: data.rentalAmount,
        paymentFrequency: data.paymentFrequency || "monthly",
        appraisedRentRangeLow: data.appraisedRentRangeLow,
        annualStatementPeriodEnd: data.annualStatementPeriodEnd,
        totalIncomeForPeriod: data.totalIncomeForPeriod,
        currency: data.currency || defaultCurrency,
        documents: existingRentalIndex >= 0 ? existingIncomes[existingRentalIndex].documents : [],
        references: references,
    };

    let updatedIncomes: LoanApplicationData["incomes"];
    if (existingRentalIndex >= 0) {
        updatedIncomes = [...existingIncomes];
        updatedIncomes[existingRentalIndex] = rentalIncomeData;
    } else {
        updatedIncomes = [...existingIncomes, rentalIncomeData];
    }

    setFieldValue("incomes", updatedIncomes);
};

const handleSelfEmployedDocument = (
    data: SelfEmployedIncome,
    currentFormData: LoanApplicationData,
    setFieldValue: SetFieldValueCallback,
    applicantId: string | null,
    defaultCurrency: string = "AUD"
): void => {
    if (applicantId === null) {
        console.log(
            `Skip payincome because applicant doesn't exist. Application: ${applicantId} DocumentId: ${data.documentId}`
        );
        return;
    }

    const existingIncomes = currentFormData.incomes || [];

    // Extract financial data from the first financial document if available
    const firstFinancial = data.financials?.[0];
    const financialYearEnd = firstFinancial?.financialYearEnd;
    const taxableIncome = firstFinancial?.taxableIncome;
    const taxesOnIncome = firstFinancial?.taxesOnIncome;

    // De-duplicate by ABN (preferred) or owner_name per applicant
    const keyABN = stripNonDigits(data.businessAbn);
    const existingIndex = existingIncomes.findIndex((inc) => {
        if (inc.id === data.id.toString()) return true;
        if (inc.incomeType !== "Self-employed") return false;
        const sameApplicant = (inc as any).applicantId ? (inc as any).applicantId === applicantId : true;
        const abnMatch = keyABN ? stripNonDigits((inc as any).businessAbn) === keyABN : false;
        const nameMatch = !keyABN ? (inc as any).businessName === data.ownerName : false;
        const sameTaxableIncome = (inc as any).taxableIncome === taxableIncome;
        const sameTaxableOnIncome = (inc as any).taxableIncome === taxableIncome;
        return sameApplicant && (abnMatch || nameMatch) && sameTaxableIncome && sameTaxableOnIncome;
    });
    let references = existingIncomes[existingIndex]?.references || new Map();
    if (firstFinancial) {
        references = extractReferences(firstFinancial, references);
    }
    references = extractReferences(data, references);
    const { abn: businessAbn, ref: abnRef } = getAbnFromContractor(
        stripNonDigits(data.businessAbn),
        currentFormData.employments || [],
        applicantId
    );
    if (abnRef && references instanceof Map) {
        references.set("businessAbn", abnRef);
    }

    const selfEmployedIncomeData = {
        id:
            existingIndex >= 0
                ? existingIncomes[existingIndex].id
                : data.id.toString() || `selfemployed_${Date.now()}_${crypto.randomUUID()}`,
        applicantId: applicantId,
        incomeType: "Self-employed" as const,
        businessStructure: normalizeBusinessStructure(data.businessStructure),
        businessAbn: businessAbn,
        businessName: titleCase(data.ownerName),
        currency: data.currency || defaultCurrency,
        financialYearEnd: financialYearEnd,
        taxableIncome: taxableIncome,
        taxesOnIncome: taxesOnIncome,
        documents: existingIndex >= 0 ? existingIncomes[existingIndex].documents : [],
        references: references,
    };

    let updatedIncomes = [...existingIncomes];
    if (existingIndex >= 0) {
        updatedIncomes[existingIndex] = selfEmployedIncomeData as any;
    } else {
        updatedIncomes = [...existingIncomes, selfEmployedIncomeData as any];
    }

    setFieldValue("incomes", updatedIncomes);
};

function handleIncomeDocument(
    incomeData: IncomePayload,
    currentFormData: LoanApplicationData,
    setFieldValue: SetFieldValueCallback,
    applicantId: string | null,
    employmentId: string | null,
    defaultCurrency: string = "AUD"
): void {
    const safeApplicantId = applicantId != null ? String(applicantId) : null;
    const safeEmploymentId = employmentId != null ? String(employmentId) : null;
    if (incomeData.incomeCategory === "PAYG income") {
        handlePAYGIncomeDocument(incomeData as PAYGIncome, currentFormData, setFieldValue, safeApplicantId, safeEmploymentId, defaultCurrency);
    } else if (incomeData.incomeCategory === "Rental income") {
        handleRentalIncomeDocument(incomeData as RentalIncome, currentFormData, setFieldValue, safeApplicantId, defaultCurrency);
    } else if (incomeData.incomeCategory === "Self-employed") {
        handleSelfEmployedDocument(incomeData as SelfEmployedIncome, currentFormData, setFieldValue, safeApplicantId, defaultCurrency);
    }
}

export { handleIncomeDocument };
